import type {
  ClassificationRuleDescriptor,
  ColumnRoleCheck,
  RegionFeatures,
  RegionTypeCheck,
  RowRoleCheck,
} from "./types";

/**
 * Classification rules.
 *
 * Each rule has:
 *   - Stable id (used as the storage key for overrides)
 *   - Display metadata (name + description)
 *   - target ('region-type' or 'row-role') and emits (which value it votes for)
 *   - Default weight + enabled flag
 *
 * The matching `check` function lives separately in `REGION_TYPE_CHECKS` /
 * `ROW_ROLE_CHECKS`, looked up by id. The descriptor list is pure data so
 * the editor UI can render and edit it without touching logic.
 *
 * Heuristic notes:
 *   - We use coarse text/numeric ratios rather than precise type detection.
 *   - Aggregation-function detection is substring-based ("SUM(" anywhere in
 *     a formula). Cheap and catches most totals; misses INDIRECT-wrapped
 *     aggregations, which is fine for a first pass.
 *   - All strength values are 0..1; final contribution = strength × weight.
 */

export const RULE_DESCRIPTORS: ClassificationRuleDescriptor[] = [
  // ===========================================================================
  // Region-type rules
  // ===========================================================================
  {
    id: "T_SINGLE_CELL",
    name: "Single cell",
    description: "Region is exactly one row by one column.",
    target: "region-type",
    emits: "single-value",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "T_VERTICAL_LIST",
    name: "Vertical list",
    description: "One column wide, multiple rows tall — a single list of values.",
    target: "region-type",
    emits: "list",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "T_HORIZONTAL_LIST",
    name: "Horizontal list",
    description: "One row tall, multiple columns wide — values in a row.",
    target: "region-type",
    emits: "list",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "T_TABLE_WITH_HEADER",
    name: "Table with header",
    description: "Top row is mostly text; rows below contain typed (mostly numeric) data.",
    target: "region-type",
    emits: "table",
    defaultWeight: 4,
    defaultEnabled: true,
  },
  {
    id: "T_FORM_2COL",
    name: "Form (key:value)",
    description: "Exactly two columns: left column text labels, right column values.",
    target: "region-type",
    emits: "form",
    defaultWeight: 4,
    defaultEnabled: true,
  },
  {
    id: "T_CROSSTAB",
    name: "Crosstab",
    description: "Top row AND left column are text labels; the interior is mostly numeric.",
    target: "region-type",
    emits: "crosstab",
    defaultWeight: 4,
    defaultEnabled: true,
  },
  {
    id: "T_TEXT_BLOCK",
    name: "Text block",
    description: "Nearly every cell is text; no numeric structure.",
    target: "region-type",
    emits: "text-block",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "T_METADATA_SPARSE",
    name: "Sparse metadata",
    description: "Region is sparsely populated with text — looks like a title block or note.",
    target: "region-type",
    emits: "metadata-block",
    defaultWeight: 2,
    defaultEnabled: true,
  },

  // ===========================================================================
  // Row-role rules
  // ===========================================================================
  {
    id: "R_HEADER_TOP_TEXT",
    name: "Header: top row, text-heavy",
    description: "First row of a multi-row region whose cells are mostly text.",
    target: "row-role",
    emits: "header",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "R_HEADER_TOP_ROW",
    name: "Header: just being the top row",
    description: "Mild bias to label the top row of any multi-row region as header.",
    target: "row-role",
    emits: "header",
    defaultWeight: 1,
    defaultEnabled: true,
  },
  {
    id: "R_SUBHEADER_SECOND_ROW",
    name: "Subheader: text row right under the header",
    description: "Row immediately below the top row is also mostly text — secondary heading.",
    target: "row-role",
    emits: "subheader",
    defaultWeight: 2,
    defaultEnabled: true,
  },
  {
    id: "R_TOTAL_GRAND_LABEL",
    name: "Total: 'Grand Total' label",
    description: "Leftmost cell contains the phrase 'grand total' (case-insensitive).",
    target: "row-role",
    emits: "total",
    defaultWeight: 6,
    defaultEnabled: true,
  },
  {
    id: "R_TOTAL_LABEL",
    name: "Total: 'Total' label",
    description: "Leftmost cell starts with 'total' (and is not 'subtotal' or 'grand total').",
    target: "row-role",
    emits: "total",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "R_SUBTOTAL_LABEL",
    name: "Subtotal: 'Subtotal' label",
    description: "Leftmost cell contains the phrase 'subtotal'.",
    target: "row-role",
    emits: "subtotal",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "R_TOTAL_BOTTOM_AGG",
    name: "Total: last row with aggregation formula",
    description: "Last row contains a SUM/AVERAGE/COUNT/SUBTOTAL formula referencing rows above.",
    target: "row-role",
    emits: "total",
    defaultWeight: 4,
    defaultEnabled: true,
  },
  {
    id: "R_SUBTOTAL_INTERIOR_AGG",
    name: "Subtotal: interior row with aggregation formula",
    description: "Non-edge row that contains an aggregation formula referencing rows above.",
    target: "row-role",
    emits: "subtotal",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "R_FOOTNOTE_TAIL_TEXT",
    name: "Footnote: trailing text row",
    description: "Bottom row(s) are all text and come after at least one numeric data row.",
    target: "row-role",
    emits: "footnote",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "R_METADATA_TOP_SPARSE",
    name: "Metadata: sparse text row at top",
    description: "Top row is mostly empty with a single text cell — looks like a title.",
    target: "row-role",
    emits: "metadata",
    defaultWeight: 2,
    defaultEnabled: true,
  },
  {
    id: "R_DATA_TYPED_ROW",
    name: "Data: row matches the typed-column pattern",
    description:
      "Row has at least one numeric or date cell (the body of a table — distinguishes data rows from text-only header/footer rows).",
    target: "row-role",
    emits: "data",
    defaultWeight: 2,
    defaultEnabled: true,
  },

  // ===========================================================================
  // Column-role rules
  // ===========================================================================
  {
    id: "C_DATE_COLUMN",
    name: "Date: column dominated by date-format cells",
    description: "At least 60% of the column's non-empty cells have a date number-format.",
    target: "column-role",
    emits: "date",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "C_CALCULATED_UNIFORM",
    name: "Calculated: uniform R1C1 formula across the column",
    description:
      "Column has formulas in most non-empty cells AND every formula cell shares the same R1C1 (a copied-down calculation).",
    target: "column-role",
    emits: "calculated",
    defaultWeight: 5,
    defaultEnabled: true,
  },
  {
    id: "C_LABEL_LEFT_OF_NUMERIC",
    name: "Label: text column with a numeric neighbour on the right",
    description:
      "Column is text-dominant AND the column immediately to its right is numeric-dominant — looks like row labels.",
    target: "column-role",
    emits: "label",
    defaultWeight: 4,
    defaultEnabled: true,
  },
  {
    id: "C_NUMERIC_DATA",
    name: "Numeric data: column dominated by plain numbers",
    description:
      "Most non-empty cells are numeric and not date-formatted; weaker if column is also a uniform-formula calculation.",
    target: "column-role",
    emits: "numeric-data",
    defaultWeight: 3,
    defaultEnabled: true,
  },
  {
    id: "C_TEXT_DATA",
    name: "Text data: column dominated by text",
    description:
      "Column is mostly text and has NO numeric neighbour to the right (so it's not just a row-label column).",
    target: "column-role",
    emits: "text-data",
    defaultWeight: 2,
    defaultEnabled: true,
  },
  {
    id: "C_IDENTIFIER_UNIQUE",
    name: "Identifier: nearly every value is distinct",
    description:
      "Column has > 95% distinct non-empty values — looks like an ID column (account number, primary key, etc.).",
    target: "column-role",
    emits: "identifier",
    defaultWeight: 3,
    defaultEnabled: true,
  },
];

// =============================================================================
// Region-type checks: features -> 0..1 strength
// =============================================================================

export const REGION_TYPE_CHECKS: Record<string, RegionTypeCheck> = {
  T_SINGLE_CELL: (f) => (f.rowCount === 1 && f.colCount === 1 ? 1 : 0),

  T_VERTICAL_LIST: (f) => (f.colCount === 1 && f.rowCount > 1 ? 1 : 0),

  T_HORIZONTAL_LIST: (f) => (f.rowCount === 1 && f.colCount > 1 ? 1 : 0),

  T_TABLE_WITH_HEADER: (f) => {
    if (f.rowCount < 2 || f.colCount < 2) return 0;
    const topTextRatio = f.rows[0].textCount / f.colCount;
    if (topTextRatio < 0.6) return 0;
    // Body rows should be typed (numeric-heavy on average)
    let bodyNumericTotal = 0;
    let bodyCells = 0;
    for (let r = 1; r < f.rowCount; r++) {
      bodyNumericTotal += f.rows[r].numericCount + f.rows[r].formulaCount;
      bodyCells += f.colCount - f.rows[r].emptyCount;
    }
    if (bodyCells === 0) return 0;
    const bodyTypedRatio = bodyNumericTotal / bodyCells;
    return Math.min(1, topTextRatio * 0.5 + bodyTypedRatio * 0.5);
  },

  T_FORM_2COL: (f) => {
    if (f.colCount !== 2 || f.rowCount < 2) return 0;
    if (f.columnTextRatio[0] < 0.6) return 0;
    const rightTyped =
      f.columnNumericRatio[1] + (1 - f.columnTextRatio[1] - (1 - f.columnNumericRatio[1]));
    // Right column should be NOT mostly text
    if (f.columnTextRatio[1] > 0.7) return 0;
    void rightTyped;
    return Math.min(1, f.columnTextRatio[0]);
  },

  T_CROSSTAB: (f) => {
    if (f.rowCount < 3 || f.colCount < 3) return 0;
    const topTextRatio = f.rows[0].textCount / f.colCount;
    const leftTextRatio = f.columnTextRatio[0];
    if (topTextRatio < 0.6 || leftTextRatio < 0.6) return 0;
    // Interior (rows 1..N, cols 1..N) should be numeric-heavy
    let interiorNumeric = 0;
    let interiorCells = 0;
    for (let r = 1; r < f.rowCount; r++) {
      for (let c = 1; c < f.colCount; c++) {
        const cat = f.rows[r].cells[c].category;
        if (cat === "empty") continue;
        interiorCells++;
        if (cat === "numeric" || cat === "formula") interiorNumeric++;
      }
    }
    if (interiorCells === 0) return 0;
    const interiorNumericRatio = interiorNumeric / interiorCells;
    return Math.min(1, topTextRatio * 0.3 + leftTextRatio * 0.3 + interiorNumericRatio * 0.4);
  },

  T_TEXT_BLOCK: (f) => {
    let total = 0;
    let text = 0;
    for (const row of f.rows) {
      for (const cell of row.cells) {
        if (cell.category === "empty") continue;
        total++;
        if (cell.category === "text") text++;
      }
    }
    if (total === 0) return 0;
    const textRatio = text / total;
    return textRatio > 0.85 ? Math.min(1, (textRatio - 0.85) / 0.15 + 0.5) : 0;
  },

  T_METADATA_SPARSE: (f) => {
    const totalCells = f.rowCount * f.colCount;
    if (totalCells < 2) return 0;
    let nonEmpty = 0;
    let text = 0;
    for (const row of f.rows) {
      for (const cell of row.cells) {
        if (cell.category !== "empty") nonEmpty++;
        if (cell.category === "text") text++;
      }
    }
    const fillRatio = nonEmpty / totalCells;
    if (fillRatio > 0.5) return 0;
    if (nonEmpty === 0) return 0;
    const textRatio = text / nonEmpty;
    if (textRatio < 0.6) return 0;
    // Sparser + more text = stronger
    return Math.min(1, (1 - fillRatio) * textRatio);
  },
};

// =============================================================================
// Row-role checks: (features, rowIndex) -> 0..1 strength
// =============================================================================

const TOTAL_LABEL_RE = /^(?:total|totals?)(?:[ :,].*)?$/i;
const GRAND_TOTAL_LABEL_RE = /^grand\s+total/i;
const SUBTOTAL_LABEL_RE = /^subtotals?(?:[ :,].*)?$/i;
const AGGREGATION_RE =
  /\b(SUM|SUMIFS?|SUMPRODUCT|AVERAGE|AVERAGEIFS?|COUNT|COUNTIFS?|COUNTA|MIN|MAX|MEDIAN|SUBTOTAL|AGGREGATE)\s*\(/i;

function rowHasAggregationFormula(row: { cells: { formula: string }[] }): boolean {
  for (const c of row.cells) {
    if (c.formula && AGGREGATION_RE.test(c.formula)) return true;
  }
  return false;
}

export const ROW_ROLE_CHECKS: Record<string, RowRoleCheck> = {
  R_HEADER_TOP_TEXT: (f, i) => {
    if (i !== 0 || f.rowCount < 2) return 0;
    const row = f.rows[0];
    const nonEmpty = f.colCount - row.emptyCount;
    if (nonEmpty === 0) return 0;
    return row.textCount / nonEmpty;
  },

  R_HEADER_TOP_ROW: (f, i) => (i === 0 && f.rowCount >= 2 ? 1 : 0),

  R_SUBHEADER_SECOND_ROW: (f, i) => {
    if (i !== 1 || f.rowCount < 3) return 0;
    const r0 = f.rows[0];
    const r1 = f.rows[1];
    if (r0.textCount / Math.max(1, f.colCount - r0.emptyCount) < 0.6) return 0;
    const r1NonEmpty = f.colCount - r1.emptyCount;
    if (r1NonEmpty === 0) return 0;
    return r1.textCount / r1NonEmpty;
  },

  R_TOTAL_GRAND_LABEL: (f, i) => (GRAND_TOTAL_LABEL_RE.test(f.rows[i].leftmostText) ? 1 : 0),

  R_TOTAL_LABEL: (f, i) => {
    const t = f.rows[i].leftmostText;
    if (GRAND_TOTAL_LABEL_RE.test(t)) return 0; // covered by R_TOTAL_GRAND_LABEL
    if (SUBTOTAL_LABEL_RE.test(t)) return 0;
    return TOTAL_LABEL_RE.test(t) ? 1 : 0;
  },

  R_SUBTOTAL_LABEL: (f, i) => (SUBTOTAL_LABEL_RE.test(f.rows[i].leftmostText) ? 1 : 0),

  R_TOTAL_BOTTOM_AGG: (f, i) => {
    if (i !== f.rowCount - 1) return 0;
    if (f.rowCount < 2) return 0;
    return rowHasAggregationFormula(f.rows[i]) ? 1 : 0;
  },

  R_SUBTOTAL_INTERIOR_AGG: (f, i) => {
    if (i === 0 || i === f.rowCount - 1) return 0;
    return rowHasAggregationFormula(f.rows[i]) ? 1 : 0;
  },

  R_FOOTNOTE_TAIL_TEXT: (f, i) => {
    if (i === 0) return 0;
    const row = f.rows[i];
    const nonEmpty = f.colCount - row.emptyCount;
    if (nonEmpty === 0) return 0;
    // Row must be all-text-ish
    if (row.textCount / nonEmpty < 0.8) return 0;
    // And must come after at least one numeric/formula row
    let sawNumericAbove = false;
    for (let r = 0; r < i; r++) {
      if (f.rows[r].numericCount + f.rows[r].formulaCount > 0) {
        sawNumericAbove = true;
        break;
      }
    }
    return sawNumericAbove ? 1 : 0;
  },

  R_METADATA_TOP_SPARSE: (f, i) => {
    if (i !== 0 || f.rowCount < 2) return 0;
    const row = f.rows[0];
    const nonEmpty = f.colCount - row.emptyCount;
    if (nonEmpty === 0 || nonEmpty >= Math.ceil(f.colCount * 0.4)) return 0;
    return row.textCount === nonEmpty ? 1 : 0;
  },

  R_DATA_TYPED_ROW: (f, i) => {
    const row = f.rows[i];
    if (row.numericCount + row.formulaCount === 0) return 0;
    // Stronger when not in extreme positions
    if (i === 0 && f.rowCount > 1) return 0.3;
    if (i === f.rowCount - 1 && f.rowCount > 1 && rowHasAggregationFormula(row)) return 0;
    return 1;
  },
};

// =============================================================================
// Column-role checks: (features, colIndex) -> 0..1 strength
// =============================================================================

export const COLUMN_ROLE_CHECKS: Record<string, ColumnRoleCheck> = {
  C_DATE_COLUMN: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount === 0) return 0;
    const ratio = col.dateCount / col.nonEmptyCount;
    return ratio >= 0.6 ? ratio : 0;
  },

  C_CALCULATED_UNIFORM: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount === 0 || !col.uniformFormula) return 0;
    const formulaShare = col.formulaCount / col.nonEmptyCount;
    return formulaShare >= 0.5 ? formulaShare : 0;
  },

  C_LABEL_LEFT_OF_NUMERIC: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount === 0) return 0;
    const textRatio = col.textCount / col.nonEmptyCount;
    if (textRatio < 0.6) return 0;
    // Look at the column immediately to the right
    if (i + 1 >= f.colCount) return 0;
    const right = f.columns[i + 1];
    if (right.nonEmptyCount === 0) return 0;
    const rightNumericRatio =
      (right.numericCount + right.dateCount + right.formulaCount) / right.nonEmptyCount;
    if (rightNumericRatio < 0.5) return 0;
    return Math.min(1, textRatio * rightNumericRatio);
  },

  C_NUMERIC_DATA: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount === 0) return 0;
    const numericRatio = (col.numericCount + col.formulaCount) / col.nonEmptyCount;
    if (numericRatio < 0.6) return 0;
    // Date columns have their own role; weaken numeric-data on date-heavy cols
    const dateRatio = col.dateCount / col.nonEmptyCount;
    return Math.max(0, numericRatio - dateRatio);
  },

  C_TEXT_DATA: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount === 0) return 0;
    const textRatio = col.textCount / col.nonEmptyCount;
    if (textRatio < 0.6) return 0;
    // No numeric neighbour on the right
    if (i + 1 < f.colCount) {
      const right = f.columns[i + 1];
      if (right.nonEmptyCount > 0) {
        const rightNumericRatio =
          (right.numericCount + right.dateCount + right.formulaCount) / right.nonEmptyCount;
        if (rightNumericRatio >= 0.5) return 0; // covered by C_LABEL_LEFT_OF_NUMERIC
      }
    }
    return textRatio;
  },

  C_IDENTIFIER_UNIQUE: (f, i) => {
    const col = f.columns[i];
    if (col.nonEmptyCount < 3) return 0;
    return col.uniqueness > 0.95 ? col.uniqueness : 0;
  },
};

/** Convenience: combined check lookup. */
export function lookupCheck(
  ruleId: string
):
  | { type: "region-type"; fn: RegionTypeCheck }
  | { type: "row-role"; fn: RowRoleCheck }
  | { type: "column-role"; fn: ColumnRoleCheck }
  | null {
  if (REGION_TYPE_CHECKS[ruleId]) return { type: "region-type", fn: REGION_TYPE_CHECKS[ruleId] };
  if (ROW_ROLE_CHECKS[ruleId]) return { type: "row-role", fn: ROW_ROLE_CHECKS[ruleId] };
  if (COLUMN_ROLE_CHECKS[ruleId]) return { type: "column-role", fn: COLUMN_ROLE_CHECKS[ruleId] };
  return null;
}

/**
 * Side-effect-free helper: confirm that every descriptor has a matching
 * check function. Use during dev to catch typos in rule ids.
 */
export function findRulesWithoutChecks(): string[] {
  const missing: string[] = [];
  for (const d of RULE_DESCRIPTORS) {
    if (lookupCheck(d.id) === null) missing.push(d.id);
  }
  return missing;
}

/**
 * RegionFeatures is the input — exported so tests / callers can construct
 * fixtures without re-importing types.
 */
export type { RegionFeatures };
